"use client";

import type React from "react";
import { useMemo, useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Chip,
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack,
  Card,
  Button,
  DialogActions,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { format } from "utils/index";
import { ShortItem } from "../types";

type PropsMap = {
  id: string;
  [key: string]: any;
};

type Props = {
  shorts: ShortItem[];
  tripsMap?: PropsMap[];
  merchant?: any;
  initialRowsPerPage?: number;
  isLoading?: boolean;
  onDelete?: (shortId?: string, shortItem?: ShortItem) => void;
  onUpdateCaption?: (shortId: string, caption: string) => void;
  selectedStatus?: string;
  onStatusChange?: (event: React.MouseEvent<HTMLElement>, newStatus: string | null) => void;
  statusOptions?: string[];
};

/**
 * Sanitize caption: remove HTML tags but preserve line breaks
 */
const sanitizeCaption = (text: string): string => {
  // Remove HTML tags
  let sanitized = text.replace(/<[^>]*>/g, "");
  // Decode HTML entities
  sanitized = sanitized
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  // Limit to 300 characters
  return sanitized.slice(0, 300);
};

const StatusChip = ({ status }: { status: string }) => {
  const s = status?.toLowerCase?.() ?? "pending";
  if (s === "approved")
    return (
      <Chip
        icon={<CheckCircleOutlineIcon />}
        label="Approved"
        variant="outlined"
        size="small"
        sx={{ borderColor: "success.main", color: "success.main" }}
      />
    );
  if (s === "rejected")
    return (
      <Chip
        icon={<CancelOutlinedIcon />}
        label="Rejected"
        variant="outlined"
        size="small"
        sx={{ borderColor: "error.main", color: "error.main" }}
      />
    );
  return (
    <Chip
      icon={<HourglassEmptyIcon />}
      label={status ?? "In-Review"}
      variant="outlined"
      size="small"
    />
  );
};

const ThumbnailCell = ({
  thumbnailUrl,
  videoUrl,
  onPlay,
}: {
  thumbnailUrl: string;
  videoUrl: string;
  onPlay: (url: string) => void;
}) => (
  <Box sx={{ position: "relative", width: 100, height: 56 }}>
    <Avatar
      variant="rounded"
      src={thumbnailUrl}
      alt="thumbnail"
      sx={{ width: 100, height: 56 }}
    />
    <Tooltip title="Play Memories">
      <IconButton
        size="small"
        onClick={() => onPlay(videoUrl)}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "rgba(255,255,255,0.7)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
        }}
      >
        <PlayArrowIcon fontSize="small" sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const ShortTableRow = ({
  row,
  idx,
  tripsMap,
  merchant,
  onPlay,
  onDelete,
  onUpdateCaption,
}: {
  row: ShortItem;
  idx: number;
  tripsMap: PropsMap[];
  merchant: any;
  onPlay: (url: string) => void;
  onDelete: (item: ShortItem) => void;
  onUpdateCaption: (item: ShortItem) => void;
}) => {
  const trip = tripsMap?.find((t) => t.id === row?.tripId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    onDelete(row);
  };

  const handleUpdateCaptionClick = () => {
    handleMenuClose();
    onUpdateCaption(row);
  };

  return (
    <TableRow hover key={row.id ?? `${row.id}-${idx}`} sx={{ height: 65 }}>
      <TableCell sx={{ width: 100, p: 1 }}>
        <ThumbnailCell
          thumbnailUrl={row?.thumbnailUrl}
          videoUrl={row?.memoriesUrl}
          onPlay={onPlay}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 150, p: 1 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500 }}
          noWrap
          title={trip?.title || row?.tripId}
        >
          {trip?.title || "N/A"}
        </Typography>
      </TableCell>

      <TableCell sx={{ minWidth: 100, p: 1 }}>
        <Typography variant="body2" noWrap title={trip?.tripType || "N/A"}>
          {trip?.tripType ?? "N/A"}
        </Typography>
      </TableCell>

      <TableCell sx={{ minWidth: 100, p: 1, textAlign: "center" }}>
        <Typography variant="body2" noWrap title={row?.caption || "N/A"}>
          {row?.caption?.slice(0, 50) ?? "N/A"}
        </Typography>
      </TableCell>

      <TableCell sx={{ minWidth: 100, p: 1 }}>
        <Typography variant="body2" noWrap title={trip?.category || "N/A"}>
          {trip?.category ?? "N/A"}
        </Typography>
      </TableCell>

      <TableCell sx={{ minWidth: 110, p: 1 }}>
        <StatusChip status={row?.memoriesStatus} />
      </TableCell>

      <TableCell sx={{ minWidth: 120, p: 1 }}>
        <Typography variant="body2">
          {format(row?.createdAt as string)}
        </Typography>
      </TableCell>

      <TableCell align="center" sx={{ width: 60, p: 1 }}>
        <Tooltip title="Actions">
          <IconButton
            size="small"
            onClick={handleMenuClick}
            aria-label="memories-actions"
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
          <MenuItem onClick={handleUpdateCaptionClick}>Update Caption</MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
            Delete Memories
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

const MobileShortCard = ({
  short,
  tripsMap,
  onPlay,
  onDelete,
  onUpdateCaption,
}: {
  short: ShortItem;
  tripsMap?: PropsMap[];
  onPlay: (url: string) => void;
  onDelete: (item: ShortItem) => void;
  onUpdateCaption: (item: ShortItem) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Find trip details
  const trip = tripsMap?.find((t) => t.id === short.tripId);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    onDelete(short);
  };

  const handleUpdateCaptionClick = () => {
    handleMenuClose();
    onUpdateCaption(short);
  };

  return (
    <Card
      key={short.id}
      sx={{
        p: 2,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Stack spacing={2}>
        <Box display="flex" gap={2} alignItems="flex-start">
          <ThumbnailCell
            thumbnailUrl={short.thumbnailUrl}
            videoUrl={short.memoriesUrl}
            onPlay={onPlay}
          />
          <Stack flex={1} spacing={1} minWidth={0}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
                noWrap
                title={trip?.title || trip?.tripTitle || short.tripId}
              >
                {trip?.title || trip?.tripTitle || short?.tripId}
              </Typography>
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                  }}
                >
                  TYPE
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.85rem" }}
                  noWrap
                  title={trip?.tripType || "N/A"}
                >
                  {trip?.tripType || "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                  }}
                >
                  CATEGORY
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.85rem" }}
                  noWrap
                  title={trip?.category || "N/A"}
                >
                  {trip?.category || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr"
          gap={1.5}
          sx={{
            "& > div": { minWidth: 0 },
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              STATUS
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <StatusChip status={short?.memoriesStatus} />
            </Box>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            pt: 1.5,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              CREATED
            </Typography>
            <Typography variant="body2">
              {format(short.createdAt as string)}
            </Typography>
          </Box>
          <Tooltip title="Actions">
            <IconButton
              size="small"
              onClick={handleMenuClick}
              aria-label="memories-actions"
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : undefined}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
            <MenuItem onClick={handleUpdateCaptionClick}>
              Update Caption
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
              Delete Memories
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
    </Card>
  );
};

const DeleteConfirmDialog = ({
  open,
  itemToDelete,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  itemToDelete: ShortItem | null;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>Delete Memories</DialogTitle>
    <DialogContent sx={{ pt: 2 }}>
      <Typography>
        Are you sure you want to delete this memories? This action cannot be
        undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button onClick={onConfirm} variant="contained" color="error">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const UpdateCaptionDialog = ({
  open,
  itemToUpdate,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  itemToUpdate: ShortItem | null;
  onConfirm: (caption: string) => void;
  onCancel: () => void;
}) => {
  const [caption, setCaption] = useState(itemToUpdate?.caption || "");

  useEffect(() => {
    setCaption(itemToUpdate?.caption || "");
  }, [itemToUpdate, open]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Update Caption</Typography>
        <IconButton size="small" onClick={onCancel} sx={{ ml: "auto" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={4}
          maxRows={6}
          placeholder="Add a caption for this memories (optional)..."
          value={caption}
          onChange={(e) => {
            if (e.target.value.length <= 300) {
              const sanitized = sanitizeCaption(e.target.value);
              setCaption(sanitized);
            }
          }}
          helperText={`${caption.length} / 300 characters`}
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": {
              fontFamily: "inherit",
              whiteSpace: "pre-wrap",
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => onConfirm(caption)}
          variant="contained"
          color="primary"
        >
          Update Caption
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VideoPlayDialog = ({
  open,
  videoUrl,
  onClose,
}: {
  open: boolean;
  videoUrl: string | null;
  onClose: () => void;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle alignSelf="center">Play Memories</DialogTitle>
    <DialogContent sx={{ pt: 2 }}>
      {videoUrl ? (
        <Box
          sx={{
            width: "100%",
            height: 360,
            bgcolor: "#000",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <video
            controls
            src={videoUrl}
            style={{ width: "100%", display: "block", height: "100%" }}
          />
        </Box>
      ) : (
        <Typography>No video available</Typography>
      )}
    </DialogContent>
  </Dialog>
);

const EmptyState = ({ isLoading }: { isLoading: boolean }) => (
  <Box sx={{ py: 8, textAlign: "center" }}>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
      {isLoading ? "Loading memories..." : "No memories found."}
    </Typography>
    {!isLoading && (
      <Typography variant="caption" color="text.secondary">
        Change the filter or try again later.
      </Typography>
    )}
  </Box>
);

export default function ShortTable({
  shorts = [],
  tripsMap = [],
  merchant,
  initialRowsPerPage = 10,
  isLoading = false,
  onDelete,
  onUpdateCaption,
  selectedStatus = "In-Review",
  onStatusChange,
  statusOptions = ["In-Review", "Approved", "Rejected"],
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [videoOpen, setVideoOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ShortItem | null>(null);
  const [updateCaptionOpen, setUpdateCaptionOpen] = useState(false);
  const [itemToUpdate, setItemToUpdate] = useState<ShortItem | null>(null);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    return shorts?.slice(start, start + rowsPerPage) ?? [];
  }, [shorts, page, rowsPerPage]);

  const openVideo = (url: string) => {
    setCurrentVideoUrl(url);
    setVideoOpen(true);
  };

  const closeVideo = () => {
    setVideoOpen(false);
    setCurrentVideoUrl(null);
  };

  const handleDeleteClick = (item: ShortItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete && onDelete) {
      onDelete(itemToDelete.id, itemToDelete);
    } else if (itemToDelete) {
      console.warn("onDelete not implemented. memories id:", itemToDelete.id);
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleUpdateCaptionClick = (item: ShortItem) => {
    setItemToUpdate(item);
    setUpdateCaptionOpen(true);
  };

  const handleConfirmUpdateCaption = (caption: string) => {
    if (itemToUpdate && onUpdateCaption) {
      onUpdateCaption(itemToUpdate.id!, caption);
    } else if (itemToUpdate) {
      console.warn(
        "onUpdateCaption not implemented. memories id:",
        itemToUpdate.id
      );
    }
    setUpdateCaptionOpen(false);
    setItemToUpdate(null);
  };

  const handleCancelUpdateCaption = () => {
    setUpdateCaptionOpen(false);
    setItemToUpdate(null);
  };

  if (isMobile) {
    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {/* Status Toggle */}
        {onStatusChange && (
          <Box sx={{ mb: 2, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <ToggleButtonGroup
              value={selectedStatus}
              exclusive
              onChange={onStatusChange}
              aria-label="status filter"
              fullWidth
              size="small"
              sx={{
                width: "100%",
                "& .MuiToggleButton-root": {
                  flex: 1,
                  textTransform: "none",
                  fontSize: "0.45rem",
                  border: "1px solid",
                  borderColor: "divider",
                  color: "text.secondary",
                  "&.Mui-selected": {
                    color: "#fff",
                    bgcolor: "primary.main",
                    borderColor: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.dark",
                      borderColor: "primary.dark",
                    },
                  },
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                },
              }}
            >
              {statusOptions.map((status) => (
                <ToggleButton key={status} value={status} aria-label={status}>
                  {status}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
        {visibleRows?.length > 0 ? (
          <>
            <Stack spacing={0}>
              {visibleRows.map((row, idx) => (
                <MobileShortCard
                  key={row.id ?? `${row.id}-${idx}`}
                  short={row}
                  tripsMap={tripsMap}
                  onPlay={openVideo}
                  onDelete={handleDeleteClick}
                  onUpdateCaption={handleUpdateCaptionClick}
                />
              ))}
            </Stack>
            <Box sx={{ mt: 2, overflow: "auto" }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={shorts?.length ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </>
        ) : (
          <EmptyState isLoading={isLoading} />
        )}
        <VideoPlayDialog
          open={videoOpen}
          videoUrl={currentVideoUrl}
          onClose={closeVideo}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", borderRadius: 2 }}>
      {/* Status Toggle */}
      {onStatusChange && (
        <Box sx={{ p: 2, pb: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
          <ToggleButtonGroup
            value={selectedStatus}
            exclusive
            onChange={onStatusChange}
            aria-label="status filter"
            sx={{
              "& .MuiToggleButton-root": {
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontSize: "0.95rem",
                border: "2px solid",
                borderColor: "divider",
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "#fff",
                  bgcolor: "primary.main",
                  borderColor: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.dark",
                    borderColor: "primary.dark",
                  },
                },
                "&:hover": {
                  bgcolor: "action.hover",
                },
              },
            }}
          >
            {statusOptions.map((status) => (
              <ToggleButton key={status} value={status} aria-label={status}>
                {status}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      <TableContainer sx={{ overflowX: isTablet ? "auto" : "visible" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              <TableCell
                sx={{ width: 100, p: 1, fontWeight: 600, fontSize: "0.875rem" }}
              >
                Memories
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 150,
                  p: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                Trip Title
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 100,
                  p: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                Type
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 100,
                  p: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  textAlign: "center",
                }}
              >
                Caption
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 100,
                  p: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                Category
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 110,
                  p: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  minWidth: 120,
                  p: 1,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                Created At
              </TableCell>
              <TableCell
                align="center"
                sx={{ width: 50, p: 1, fontWeight: 600, fontSize: "0.875rem" }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows?.length > 0 ? (
              visibleRows.map((row, idx) => (
                <ShortTableRow
                  key={row.id ?? `${row.id}-${idx}`}
                  row={row}
                  idx={idx}
                  tripsMap={tripsMap}
                  merchant={merchant}
                  onPlay={openVideo}
                  onDelete={handleDeleteClick}
                  onUpdateCaption={handleUpdateCaptionClick}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} sx={{ border: 0 }}>
                  <EmptyState isLoading={isLoading} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {shorts?.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={shorts?.length ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      <VideoPlayDialog
        open={videoOpen}
        videoUrl={currentVideoUrl}
        onClose={closeVideo}
      />
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        itemToDelete={itemToDelete}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <UpdateCaptionDialog
        open={updateCaptionOpen}
        itemToUpdate={itemToUpdate}
        onConfirm={handleConfirmUpdateCaption}
        onCancel={handleCancelUpdateCaption}
      />
    </Paper>
  );
}
